import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import IDHelpers from '../../../core/helpers/IDHelper';
import { Block } from '../../components';
import { CallToActionButton, SummaryList } from './components';
import CardRequest from './components/Cards/request';
import LinkProcedureAmount from './components/LinkProcedureAmount';
import Spinner from '../../components/Spinner';
import { MainContainer } from '../common/CardBoardStyled';
import EmptyStateProcedureMessage from './components/EmptyStateProcedureMessage';
import InputSearch from '../../reusables/InputSearch';
import SectionHeader from '@/presentation/reusables/SectionHeader';
import Category from './components/Lists/Category';
import ModuleContainer from '@/presentation/components/Container';
import ButtonEdge from '@/presentation/reusables/Button/Edge';

interface ProceduresViewProps {
  handleSelectCategory: any;
  handleSelectRequest: any;
  myProceduresList: any;
  categoryList: any;
  proceduresListIsLoading: any;
  loadingDataNoAutomated: boolean;
  itemsSearch: any[];
  searchText: string;
  setSearchText: (text: string) => void;
  selectItemFound: (item: any) => void;
  sectionHeader: any;
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 24px;

  .input {
    @media (min-width: 640px) {
      width: 456px;
    }
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0 24px 0;
  background-color: #d5e5f0;

  @media (max-width: 767px) {
    margin-left: -16px;
    margin-right: -16px;
    width: -webkit-fill-available !important;
  }

  @media (min-width: 768px) and (max-width: 991px) {
    margin-left: -24px;
    margin-right: -24px;
    width: -webkit-fill-available !important;
  }

  @media (min-width: 992px) {
    margin-left: -32px;
    margin-right: -32px;
    width: -webkit-fill-available !important;
  }

  > div {
    // space progressive
    @media (min-width: 1280px) {
      padding-left: calc((100vw - 1215px) * 0.5);
      padding-right: calc((100vw - 1215px) * 0.5);
    }
  }

  .history {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 767px) {
      margin-left: 16px;
      margin-right: 16px;
    }

    @media (min-width: 768px) and (max-width: 991px) {
      margin-left: 24px;
      margin-right: 24px;
    }

    @media (min-width: 992px) {
      margin-left: 32px;
      margin-right: 32px;
      padding: 0;
    }

    @media (min-width: 1281px) {
      margin-left: 0;
      margin-right: 0;
      padding-left: calc((100vw - 1215px) * 0.5);
      padding-right: calc((100vw - 1215px) * 0.5);
    }
  }

  .card {
    @media (max-width: 767px) {
      margin-left: 16px;
    }

    @media (min-width: 768px) and (max-width: 991px) {
      margin-left: 24px !important;
    }

    @media (min-width: 992px) {
      margin-left: 32px;
      margin-right: 32px;
      padding: 0;
    }

    @media (min-width: 1281px) {
      margin-left: 0;
      margin-right: 0;
      padding-left: calc((100vw - 1215px) * 0.5);
      padding-right: calc((100vw - 1215px) * 0.5);
    }
  }
`;

const ProceduresView = (props: ProceduresViewProps) => {
  const {
    handleSelectCategory,
    handleSelectRequest,
    myProceduresList,
    categoryList,
    sectionHeader,
    proceduresListIsLoading,
    loadingDataNoAutomated,
    itemsSearch,
    searchText,
    setSearchText,
    selectItemFound
  } = props;

  const history = useHistory();
  const tabIndexButton = 7;

  return (
    <ModuleContainer containerType="section">
      <SectionHeader
        title={sectionHeader.title}
        description={sectionHeader.description}
      />
      <InputContainer>
        <div className="input">
          <InputSearch
            value={searchText}
            onChange={setSearchText}
            isLoadingData={loadingDataNoAutomated}
            disabled={loadingDataNoAutomated}
            filterMetadata={itemsSearch}
            selectItem={selectItemFound}
            tabIndexInput={1}
            tabIndexButton={2}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Category
            categoryList={categoryList}
            handleSelectCategory={handleSelectCategory}
          />
          <LinkProcedureAmount tabIndexButton={6} />
        </div>
      </InputContainer>
      <CardContainer>
        <div className="history">
          <p
            style={{
              fontWeight: 900,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#161d1f'
            }}
          >
            Estado de tus trámites
          </p>
          {myProceduresList?.length ? (
            <ButtonEdge
              buttonType="viewHistory"
              tabIndex={tabIndexButton}
              onClick={() => history.push('/tramites/estudiante/historico')}
            />
          ) : (
            <></>
          )}
        </div>
        <div className="card">
          <div className="procedures">
            {proceduresListIsLoading && <Spinner />}
            {!proceduresListIsLoading && (
              <SummaryList>
                <Block
                  customized
                  justifyContent="flex-start"
                  className="gridSummaries"
                >
                  {myProceduresList && myProceduresList?.length === 0 ? (
                    <MainContainer>
                      <div className="defaultState">
                        <EmptyStateProcedureMessage />
                      </div>
                    </MainContainer>
                  ) : (
                    myProceduresList?.map((myProcedure: any, index: number) => (
                      <CardRequest
                        key={IDHelpers.generateID()}
                        myProcedure={myProcedure}
                        handleClick={() => {
                          handleSelectRequest(
                            myProcedure.id,
                            myProcedure.procedureId
                          );
                        }}
                        tabIndexButton={8 + index}
                      />
                    ))
                  )}
                </Block>
              </SummaryList>
            )}
          </div>
        </div>
      </CardContainer>
      <CallToActionButton />
    </ModuleContainer>
  );
};

export default ProceduresView;
